import React, { useEffect, useMemo, useRef } from 'react';
import maplibregl from 'maplibre-gl/dist/maplibre-gl.js';

const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY;
const MAPTILER_STYLE_URL = process.env.REACT_APP_MAPTILER_STYLE_URL;

function buildMaptilerStyleUrl(styleUrl, key) {
  if (!styleUrl || !key) return null;
  const raw = String(styleUrl).trim();
  if (!raw) return null;
  if (raw.includes('{key}')) return raw.replace('{key}', encodeURIComponent(key));
  if (/([?&])key=/.test(raw)) return raw;
  return raw.includes('?') ? `${raw}&key=${encodeURIComponent(key)}` : `${raw}?key=${encodeURIComponent(key)}`;
}

const MAPLIBRE_STYLE_URL =
  buildMaptilerStyleUrl(MAPTILER_STYLE_URL, MAPTILER_KEY) ||
  (MAPTILER_KEY ? `https://api.maptiler.com/maps/streets-v2/style.json?key=${encodeURIComponent(MAPTILER_KEY)}` : null) ||
  'https://demotiles.maplibre.org/style.json';

function isFiniteCoordPair(pair) {
  return Array.isArray(pair) && pair.length >= 2 && Number.isFinite(Number(pair[0])) && Number.isFinite(Number(pair[1]));
}

function makeLineFeature(coordinates, properties = {}) {
  const validCoordinates = (Array.isArray(coordinates) ? coordinates : [])
    .filter(isFiniteCoordPair)
    .map((pair) => [Number(pair[0]), Number(pair[1])]);
  if (validCoordinates.length < 2) return null;
  return {
    type: 'Feature',
    properties,
    geometry: {
      type: 'LineString',
      coordinates: validCoordinates,
    },
  };
}

function getGeometryFeature(input, fallbackFrom, fallbackTo) {
  const fallbackFeature =
    fallbackFrom && fallbackTo && Number.isFinite(Number(fallbackFrom.lat)) && Number.isFinite(Number(fallbackFrom.lng)) && Number.isFinite(Number(fallbackTo.lat)) && Number.isFinite(Number(fallbackTo.lng))
      ? makeLineFeature([
          [Number(fallbackFrom.lng), Number(fallbackFrom.lat)],
          [Number(fallbackTo.lng), Number(fallbackTo.lat)],
        ], { fallback: true })
      : null;

  const visit = (geometry) => {
    if (!geometry) return null;

    if (Array.isArray(geometry)) {
      for (const item of geometry) {
        const found = visit(item);
        if (found) return found;
      }
      return null;
    }

    if (typeof geometry === 'string') {
      try {
        const parsed = JSON.parse(geometry);
        return visit(parsed);
      } catch {
        return null;
      }
    }

    if (typeof geometry !== 'object') return null;

    if (geometry.type === 'LineString' && Array.isArray(geometry.coordinates)) {
      return makeLineFeature(geometry.coordinates, geometry.properties || {});
    }

    if (geometry.type === 'MultiLineString' && Array.isArray(geometry.coordinates)) {
      const longestLine = geometry.coordinates
        .filter((line) => Array.isArray(line))
        .sort((a, b) => b.length - a.length)[0];
      return makeLineFeature(longestLine, geometry.properties || {});
    }

    if (geometry.type === 'Feature') {
      return visit(geometry.geometry) || visit(geometry.properties?.geometry) || visit(geometry.properties?.route);
    }

    if (geometry.type === 'FeatureCollection' && Array.isArray(geometry.features)) {
      return visit(geometry.features);
    }

    return visit(geometry.geometry) || visit(geometry.route) || visit(geometry.routes) || visit(geometry.features) || visit(geometry.path);
  };

  return visit(input) || fallbackFeature;
}

function computeBoundsFromCoords(coordsList) {
  const valid = (Array.isArray(coordsList) ? coordsList : [])
    .map((c) => (c && c.lat != null && c.lng != null ? [Number(c.lng), Number(c.lat)] : null))
    .filter((c) => c && Number.isFinite(c[0]) && Number.isFinite(c[1]));
  if (valid.length === 0) return null;
  let minLng = valid[0][0];
  let maxLng = valid[0][0];
  let minLat = valid[0][1];
  let maxLat = valid[0][1];
  for (const [lng, lat] of valid) {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  return {
    sw: [minLng, minLat],
    ne: [maxLng, maxLat]
  };
}

function computeBoundsFromLineString(feature) {
  const coords = feature && feature.geometry && Array.isArray(feature.geometry.coordinates)
    ? feature.geometry.coordinates
    : [];
  const valid = coords
    .map((c) => (Array.isArray(c) && c.length >= 2 ? [Number(c[0]), Number(c[1])] : null))
    .filter((c) => c && Number.isFinite(c[0]) && Number.isFinite(c[1]));
  if (valid.length === 0) return null;
  let minLng = valid[0][0];
  let maxLng = valid[0][0];
  let minLat = valid[0][1];
  let maxLat = valid[0][1];
  for (const [lng, lat] of valid) {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  return {
    sw: [minLng, minLat],
    ne: [maxLng, maxLat]
  };
}

export default function TransportMapLibre({
  view,
  fromCoords,
  toCoords,
  driverCoords,
  routeGeometry,
  pickEnabled,
  pickedCoords,
  onPick,
  className,
  style
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({ from: null, to: null, driver: null, picked: null });
  const initialCenterRef = useRef(null);
  const routeFeature = useMemo(() => getGeometryFeature(routeGeometry, fromCoords, toCoords), [routeGeometry, fromCoords, toCoords]);

  const center = useMemo(() => {
    const seed =
      (pickedCoords && Number.isFinite(Number(pickedCoords.lat)) && Number.isFinite(Number(pickedCoords.lng))
        ? { lat: Number(pickedCoords.lat), lng: Number(pickedCoords.lng) }
        : null) ||
      (driverCoords && Number.isFinite(Number(driverCoords.lat)) && Number.isFinite(Number(driverCoords.lng))
        ? { lat: Number(driverCoords.lat), lng: Number(driverCoords.lng) }
        : null) ||
      (fromCoords && Number.isFinite(Number(fromCoords.lat)) && Number.isFinite(Number(fromCoords.lng))
        ? { lat: Number(fromCoords.lat), lng: Number(fromCoords.lng) }
        : null) ||
      (toCoords && Number.isFinite(Number(toCoords.lat)) && Number.isFinite(Number(toCoords.lng))
        ? { lat: Number(toCoords.lat), lng: Number(toCoords.lng) }
        : null);
    return seed ? [seed.lng, seed.lat] : [-66.9036, 10.4806];
  }, [pickedCoords, driverCoords, fromCoords, toCoords]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) return;

    if (!initialCenterRef.current) {
      initialCenterRef.current = center;
    }

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAPLIBRE_STYLE_URL,
      center: initialCenterRef.current,
      zoom: 13,
      attributionControl: false
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    return () => {
      try {
        map.remove();
      } catch {
      }
      mapRef.current = null;
      markersRef.current = { from: null, to: null, driver: null, picked: null };
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const ensureMarker = (key, coords, color) => {
      const has = coords && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.lng));
      if (!has) {
        if (markersRef.current[key]) {
          try {
            markersRef.current[key].remove();
          } catch {
          }
          markersRef.current[key] = null;
        }
        return;
      }

      const lngLat = [Number(coords.lng), Number(coords.lat)];
      if (!markersRef.current[key]) {
        const el = document.createElement('div');
        el.style.width = '12px';
        el.style.height = '12px';
        el.style.borderRadius = '9999px';
        el.style.background = color;
        el.style.border = '2px solid rgba(15, 23, 42, 0.9)';
        el.style.boxShadow = '0 0 0 2px rgba(255,255,255,0.08)';

        markersRef.current[key] = new maplibregl.Marker({ element: el })
          .setLngLat(lngLat)
          .addTo(map);
      } else {
        markersRef.current[key].setLngLat(lngLat);
      }
    };

    ensureMarker('from', fromCoords, '#22d3ee');
    ensureMarker('to', toCoords, '#f97316');
    ensureMarker('driver', driverCoords, '#a78bfa');
    ensureMarker('picked', pickedCoords, '#f97316');
  }, [fromCoords, toCoords, driverCoords, pickedCoords]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const sourceId = 'mx-transport-route';
    const layerId = 'mx-transport-route-line';

    const apply = () => {
      if (!routeFeature) {
        if (map.getLayer(layerId)) {
          try {
            map.removeLayer(layerId);
          } catch {
          }
        }
        if (map.getSource(sourceId)) {
          try {
            map.removeSource(sourceId);
          } catch {
          }
        }
        return;
      }

      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: 'geojson',
          data: routeFeature
        });
      } else {
        try {
          map.getSource(sourceId).setData(routeFeature);
        } catch {
        }
      }

      if (!map.getLayer(layerId)) {
        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#22d3ee',
            'line-width': 5,
            'line-opacity': 0.9
          }
        });
      }
    };

    if (map.isStyleLoaded()) {
      apply();
      return;
    }

    map.once('load', apply);
    return () => {
      try {
        map.off('load', apply);
      } catch {
      }
    };
  }, [routeFeature]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const doFit = () => {
      let bounds = null;

      if (view === 'route' && routeFeature) {
        bounds = computeBoundsFromLineString(routeFeature);
      }

      if (!bounds && view === 'driver') {
        bounds = computeBoundsFromCoords([driverCoords]);
      }

      if (!bounds && view === 'destination') {
        bounds = computeBoundsFromCoords([toCoords]);
      }

      if (!bounds && pickEnabled) {
        bounds = computeBoundsFromCoords([pickedCoords]);
      }

      if (!bounds) {
        bounds = computeBoundsFromCoords([fromCoords, toCoords, driverCoords, pickedCoords]);
      }

      if (!bounds) {
        map.jumpTo({ center, zoom: 13 });
        return;
      }

      const sw = bounds.sw;
      const ne = bounds.ne;
      if (!sw || !ne) return;

      const samePoint = sw[0] === ne[0] && sw[1] === ne[1];
      if (samePoint) {
        map.easeTo({ center: sw, zoom: Math.max(map.getZoom(), 15), duration: 250 });
        return;
      }

      map.fitBounds([sw, ne], { padding: 40, duration: 300 });
    };

    if (map.isStyleLoaded()) {
      doFit();
      return;
    }

    map.once('load', doFit);
    return () => {
      try {
        map.off('load', doFit);
      } catch {
      }
    };
  }, [center, driverCoords, fromCoords, pickEnabled, pickedCoords, routeFeature, toCoords, view]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!pickEnabled) return;

    const handler = (e) => {
      const lngLat = e && e.lngLat ? e.lngLat : null;
      if (!lngLat) return;
      const coords = { lat: Number(lngLat.lat), lng: Number(lngLat.lng) };
      onPick?.(coords);
    };

    map.on('click', handler);
    return () => {
      try {
        map.off('click', handler);
      } catch {
      }
    };
  }, [pickEnabled, onPick]);

  return <div ref={containerRef} className={className || 'w-full h-full'} style={style} />;
}
